import { useState, useEffect } from "react";
import { Alert, Disclaimer, CustomTooltip } from "../../components";
import { usePageLoadingContext } from "../../contexts/PageLoadingContext";
import { useSelector } from "react-redux";
import store, { RootState } from "../../store";
import { SaveQuoteRequest, Lob } from "../../api/types";
import { useApi } from "../../api";
import { API_ERROR_MESSAGE } from "../../api/launchpad-api";
import { AgentBundlesSection } from "../../components/agent-bundles-section";
import { ZipSelectionComponent } from "../../components/zip-selection";
import { BusinessKeywordUkwComponent } from "../../components/business-keyword-agent-ukw";
import { AgentProductsSection } from "../../components/agent-products-section";
import YesNoRadioButtonsField from "../../components/yes-no-radio-buttons-field";
import {
  ALL_CARRIER_RECOMMENDATION_RESPONSE,
  LineOfBusinessCarrierInfo,
} from "../../api/types/all-carrier-recommendation-response";
import { getProductByLob } from "../../config/product-config";
import { LogoAttributes } from "../../config/types/product-config-settings";
import { RecommendedCarrier } from "../../api/types/carrier-recommendation-model";
import {
  defaultBundles,
  geicoCauInfo,
  lobOrder,
} from "../../config/partner-config/agent-partner-select-constants";
import { getRedirectMessage } from "../../config/quote-outcome/redirectAlertMessages";
import { PopupModal } from "../../components/popup-modal";
import { Button } from "../../components/button";
import { BINDING_RESTRICTIONS_RESPONSE } from "../../api/types/binding-restrictions-model";
import { useClientConfigs } from "../../contexts/ClientConfigContext";
import { filterCarriersByVendorFlag, isVendorAllowedByFlag, sortRedirectableVendorsFirst } from "../../utils/vendor-filter";

export interface lobToCardInfo {
  lobName: string;
  cardInfo: ProductCardInfo[];
  SelectedProducts: Lob[];
  isEnabled: boolean;
  logoAttributes: LogoAttributes;
}

export interface lobToBundleCardInfo {
  lobName: string;
  cardInfo: bundleCardInfo;
  isEnabled: boolean;
}
export interface ProductCardInfo {
  businessClass: string;
  carrier: string;
  carrierBusinessClass: string;
  label: string;
  isBoldPenguinOverride: boolean;
  isRedirectable: boolean;
}

export interface bundleCardInfo {
  firstLobCarriers: ProductCardInfo[];
  firstLobSelectedProducts: Lob[];
  firstLobLogoAttributes: LogoAttributes;
  secondLobCarriers: ProductCardInfo[];
  secondLobSelectedProducts: Lob[];
  secondLobLogoAttributes: LogoAttributes;
}

function sortLobsByOrder(
  data: LineOfBusinessCarrierInfo[]
): LineOfBusinessCarrierInfo[] {
  return data.sort(
    (a, b) =>
      lobOrder.indexOf(a.lineOfBusiness) - lobOrder.indexOf(b.lineOfBusiness)
  );
}

function convertToLobMap(
  data: LineOfBusinessCarrierInfo[]
): Map<string, RecommendedCarrier[]> {
  const lobMap = new Map<string, RecommendedCarrier[]>();
  data.forEach((lob) => {
    lobMap.set(lob.lineOfBusiness, lob.carrierInfos);
  });
  return lobMap;
}

function toProductCardInfo(
  carrier: RecommendedCarrier,
  lob: string,
  redirectableVendors?: Record<string, string[]>,
  labelToVendorCode: Record<string, string> = {}
): ProductCardInfo {
  return {
    businessClass: carrier.businessClass,
    carrier: carrier.carrier,
    carrierBusinessClass: carrier.carrierBusinessClass,
    label: carrier.label,
    isBoldPenguinOverride: carrier.isBoldPenguinOverride,
    isRedirectable: isVendorAllowedByFlag(carrier, lob, redirectableVendors, labelToVendorCode),
  };
}

function generateLobCards(
  lobMap: Map<string, RecommendedCarrier[]>,
  redirectableVendors?: Record<string, string[]>,
  labelToVendorCode: Record<string, string> = {}
): lobToCardInfo[] {
  return Array.from(lobMap.entries())
    .filter(([lobName]) => lobName !== "OWNERSONLYWC") // Exclude OWNERSONLYWC from direct rendering
    .map(([lobName, carriers]) => {
      const lobInfo = getProductByLob(lobName);
      return {
        lobName: lobInfo.label,
        cardInfo: carriers.map((carrier) =>
          toProductCardInfo(carrier, lobName, redirectableVendors, labelToVendorCode)
        ),
        SelectedProducts: lobInfo
          ? [
            {
              LobCode: lobInfo.lobCode,
              LobName: lobInfo.lobName,
              SspCode: lobInfo.sspCode,
              AdobeDtmName: lobInfo.adobeDtmName,
            },
          ]
          : [],
        isEnabled: true,
        logoAttributes: lobInfo.logoAttributes || {},
      };
    });
}

function generateBundleCards(
  lobMap: Map<string, RecommendedCarrier[]>,
  defaultBundles: [string, string][],
  redirectableVendors?: Record<string, string[]>,
  labelToVendorCode: Record<string, string> = {}
): lobToBundleCardInfo[] {
  return defaultBundles
    .map(([lob1, lob2]) => {
      const lobInfo1 = getProductByLob(lob1);
      const lobInfo2 = getProductByLob(lob2);

      const bundleCarriers: bundleCardInfo = {
        firstLobCarriers: [],
        firstLobSelectedProducts: [
          {
            LobCode: lobInfo1.lobCode,
            LobName: lobInfo1.lobName,
            SspCode: lobInfo1.sspCode,
            AdobeDtmName: lobInfo1.adobeDtmName,
          },
        ],
        firstLobLogoAttributes: lobInfo1.logoAttributes || {},
        secondLobCarriers: [],
        secondLobSelectedProducts: [
          {
            LobCode: lobInfo2.lobCode,
            LobName: lobInfo2.lobName,
            SspCode: lobInfo2.sspCode,
            AdobeDtmName: lobInfo2.adobeDtmName,
          },
        ],
        secondLobLogoAttributes: lobInfo2.logoAttributes || {},
      };

      lobMap.get(lob1)?.forEach((carrier) => {
        bundleCarriers.firstLobCarriers.push(
          toProductCardInfo(carrier, lob1, redirectableVendors, labelToVendorCode)
        );
      });

      lobMap.get(lob2)?.forEach((carrier) => {
        bundleCarriers.secondLobCarriers.push(
          toProductCardInfo(carrier, lob2, redirectableVendors, labelToVendorCode)
        );
      });

      if (
        bundleCarriers.firstLobCarriers.length > 0 &&
        bundleCarriers.secondLobCarriers.length > 0
      ) {
        return {
          lobName: `${lobInfo1.label} + ${lobInfo2.label}`,
          cardInfo: bundleCarriers,
          isEnabled: true,
        };
      }
      return null;
    })
    .filter((bundle): bundle is lobToBundleCardInfo => bundle !== null);
}

export const AgentPartnerSelect = () => {
  const api = useApi();
  const clientConfig = useClientConfigs();
  const [showQuestionsAlert, setShowQuestionsAlert] = useState(false);
  const [questionsAlertMessage, setQuestionsAlertMessage] = useState({
    title: "",
    message: "",
  });
  const [showProductsAlert, setShowProductsAlert] = useState(false);
  const [productsAlertMessage, setProductsAlertMessage] = useState({
    title: "",
    message: "",
  });
  const quote = useSelector((state: RootState) => state.quote.quote);
  const { showLoader, hideLoader } = usePageLoadingContext();
  const [isBOPAvailable, setIsBOPAvailable] = useState<boolean | undefined>(
    undefined
  );
  const [isWCAvailable, setIsWCAvailable] = useState<boolean | undefined>(
    undefined
  );
  const [isOwnersWCAvailable, setIsOwnersWCAvailable] = useState<boolean | undefined>(
    undefined
  );
  const [showProducts, setShowProducts] = useState<boolean>(false);
  const [baseBundlesInfo, setBaseBundlesInfo] = useState<lobToBundleCardInfo[]>([]);
  const [baseProductsInfo, setBaseProductsInfo] = useState<lobToCardInfo[]>([]);
  const [bundlesInfo, setBundlesInfo] = useState<lobToBundleCardInfo[]>([]);
  const [productsInfo, setProductsInfo] = useState<lobToCardInfo[]>([]);
  const [showRedirect, setShowRedirect] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState<{ title: string; message: React.ReactNode }>({ title: "", message: "" });
  const redirectAlertMessages = getRedirectMessage;
  const [modalFooter, setModalFooter] = useState<React.ReactNode>(null);
  const [onModalClose, setOnModalClose] = useState<() => void>(() => () => { });
  const [hasBindingRestriction, setHasBindingRestriction] = useState(false);
  const [ownersOnlyWcData, setOwnersOnlyWcData] = useState<RecommendedCarrier[] | null>(null);
  const [regularWcData, setRegularWcData] = useState<RecommendedCarrier[] | null>(null);

  useEffect(() => {
    if (quote.data.Zip && quote.data.StateCode) {
      api.commercialAgentApi.getBindingRestrictions(quote.data.Zip)
        .then((bindingResponse) => {
          setHasBindingRestriction(
            bindingResponse.type === BINDING_RESTRICTIONS_RESPONSE && bindingResponse.data?.isEligible === false
          );
        });
    }
  }, [quote.data.StateCode, quote.data.Zip]);

  // Reset owner-only WC when regular WC becomes available
  useEffect(() => {
    if (isWCAvailable === true && isOwnersWCAvailable !== undefined) {
      setIsOwnersWCAvailable(undefined);
    }
  }, [isWCAvailable]);

  useEffect(() => {
    if (
      quote.data.BusinessKeyword &&
      quote.data.BusinessSecondaryKeyword &&
      quote.data.Zip.length === 5 &&
      quote.data.StateCode
    ) {
      showLoader();

      setIsBOPAvailable(undefined);
      setIsWCAvailable(undefined);
      setIsOwnersWCAvailable(undefined);
      let saveQuote: SaveQuoteRequest = { ...quote.data };
      api.commercialAgentApi
        .getAllLobMultipleCarrierRecommendationUkw(saveQuote)
        .then(
          (response) => {
            if (response.type !== ALL_CARRIER_RECOMMENDATION_RESPONSE) {
              window.scrollTo(0, 0);
              setProductsAlertMessage({
                title: "Thank you for your request.",
                message: API_ERROR_MESSAGE,
              });
              setShowProductsAlert(true);
              hideLoader();
              return;
            } else {
              const updatedData = addGeicoToCau(response.data);
              const sortedCarriers = sortLobsByOrder(updatedData);
              const lobMap = convertToLobMap(sortedCarriers);
              const filteredLobMap = filterCarriersByVendorFlag(lobMap, clientConfig.availableVendorsAgent, clientConfig.vendorLabelMap);
              const orderedLobMap = sortRedirectableVendorsFirst(
                filteredLobMap,
                clientConfig.redirectableVendorsAgent,
                clientConfig.vendorLabelMap
              );

              // Store WC and OWNERSONLYWC data separately
              if (orderedLobMap.has("WC")) {
                setRegularWcData(orderedLobMap.get("WC") || null);
              }
              if (orderedLobMap.has("OWNERSONLYWC")) {
                setOwnersOnlyWcData(orderedLobMap.get("OWNERSONLYWC") || null);
              }

              const lobToCardsInfo = generateLobCards(
                orderedLobMap,
                clientConfig.redirectableVendorsAgent,
                clientConfig.vendorLabelMap
              );
              const bundlesInfos = generateBundleCards(
                orderedLobMap,
                defaultBundles,
                clientConfig.redirectableVendorsAgent,
                clientConfig.vendorLabelMap
              );

              setBaseProductsInfo(lobToCardsInfo);
              setBaseBundlesInfo(bundlesInfos);

              setShowProducts(true);
              hideLoader();
            }
          },
          (error) => {
            setProductsAlertMessage({
              title: "Thank you for your request.",
              message: API_ERROR_MESSAGE,
            });
            setShowProductsAlert(true);
            hideLoader();
          }
        );
    } else {
      setBaseProductsInfo([]);
      setBaseBundlesInfo([]);
      setShowProducts(false);
    }
  }, [
    quote.data.Zip,
    quote.data.StateCode,
    quote.data.BusinessSecondaryKeyword,
  ]);

  useEffect(() => {
    if (baseProductsInfo.length === 0) return;

    const createWcCardInfo = (carrierData: RecommendedCarrier[], lob: string) =>
      carrierData.map((carrier) =>
        toProductCardInfo(
          carrier,
          lob,
          clientConfig.redirectableVendorsAgent,
          clientConfig.vendorLabelMap
        )
      );

    const createWcSelectedProducts = () => {
      const wcLobInfo = getProductByLob("WC");
      return [
        {
          LobCode: wcLobInfo.lobCode,
          LobName: wcLobInfo.lobName,
          SspCode: wcLobInfo.sspCode,
          AdobeDtmName: wcLobInfo.adobeDtmName,
        },
      ];
    };

    const updateWcInProducts = (
      products: lobToCardInfo[],
      wcData: RecommendedCarrier[],
      lob: string
    ): lobToCardInfo[] => {
      return products.map((lobInfo) => {
        if (lobInfo.lobName === "Workers' Compensation") {
          return {
            ...lobInfo,
            cardInfo: createWcCardInfo(wcData, lob),
            SelectedProducts: createWcSelectedProducts(),
          };
        }
        return lobInfo;
      });
    };

    const createBundleCarrierInfo = (wcData: RecommendedCarrier[], lob: string) =>
      wcData.map((carrier) =>
        toProductCardInfo(
          carrier,
          lob,
          clientConfig.redirectableVendorsAgent,
          clientConfig.vendorLabelMap
        )
      );

    const updateWcInBundles = (
      bundles: lobToBundleCardInfo[],
      wcData: RecommendedCarrier[],
      lob: string
    ): lobToBundleCardInfo[] => {
      return bundles.map((bundleInfo) => {
        if (!bundleInfo.lobName.includes("Workers' Compensation")) {
          return bundleInfo;
        }

        const isFirstLobWc = bundleInfo.cardInfo.firstLobLogoAttributes.className?.includes("workers-comp");
        const isSecondLobWc = bundleInfo.cardInfo.secondLobLogoAttributes.className?.includes("workers-comp");

        if (isFirstLobWc) {
          return {
            ...bundleInfo,
            cardInfo: {
              ...bundleInfo.cardInfo,
              firstLobCarriers: createBundleCarrierInfo(wcData, lob),
              firstLobSelectedProducts: createWcSelectedProducts(),
            },
          };
        } else if (isSecondLobWc) {
          return {
            ...bundleInfo,
            cardInfo: {
              ...bundleInfo.cardInfo,
              secondLobCarriers: createBundleCarrierInfo(wcData, lob),
              secondLobSelectedProducts: createWcSelectedProducts(),
            },
          };
        }
        return bundleInfo;
      });
    };

    let workingProductsInfo = [...baseProductsInfo];
    let workingBundlesInfo = [...baseBundlesInfo];

    // Handle WC vs OWNERSONLYWC swap based on isOwnersWCAvailable
    if (isOwnersWCAvailable === true && ownersOnlyWcData) {
      // User needs owner-only WC - use OWNERSONLYWC data
      workingProductsInfo = updateWcInProducts(workingProductsInfo, ownersOnlyWcData, "OWNERSONLYWC");
      workingBundlesInfo = updateWcInBundles(workingBundlesInfo, ownersOnlyWcData, "OWNERSONLYWC");
    } else if ((isOwnersWCAvailable === false || isOwnersWCAvailable === undefined) && regularWcData) {
      // User doesn't need owner-only WC or question is hidden - use regular WC data
      workingProductsInfo = updateWcInProducts(workingProductsInfo, regularWcData, "WC");
      workingBundlesInfo = updateWcInBundles(workingBundlesInfo, regularWcData, "WC");
    }

    // Update isEnabled flags
    const updatedProductsInfo = workingProductsInfo.map((lobInfo) => ({
      ...lobInfo,
      isEnabled:
        (lobInfo.lobName === "Business Owner's" &&
          (isBOPAvailable ?? true)) ||
        (lobInfo.lobName === "Workers' Compensation" &&
          ((isWCAvailable ?? true) || (isWCAvailable === false && isOwnersWCAvailable === true))) ||
        (lobInfo.lobName !== "Business Owner's" &&
          lobInfo.lobName !== "Workers' Compensation"),
    }));

    const updatedBundlesInfo = workingBundlesInfo.map((bundleInfo) => ({
      ...bundleInfo,
      isEnabled:
        (bundleInfo.lobName.includes("Business Owner's") &&
          (isBOPAvailable ?? true)) ||
        (bundleInfo.lobName.includes("Workers' Compensation") &&
          ((isWCAvailable ?? true) || (isWCAvailable === false && isOwnersWCAvailable === true))) ||
        (!bundleInfo.lobName.includes("Business Owner's") &&
          !bundleInfo.lobName.includes("Workers' Compensation")),
    }));

    setProductsInfo(updatedProductsInfo);
    setBundlesInfo(updatedBundlesInfo);
  }, [baseProductsInfo, baseBundlesInfo, isBOPAvailable, isWCAvailable, isOwnersWCAvailable, ownersOnlyWcData, regularWcData]);

  function clearAlertMessage(
    setAlertMessage: (message: { title: string; message: string }) => void
  ) {
    setAlertMessage({ title: "", message: "" });
  }

 function addGeicoToCau(data: LineOfBusinessCarrierInfo[]): LineOfBusinessCarrierInfo[] {
  return data
    .filter(lob => lob.carrierInfos && lob.carrierInfos.length > 0) // Remove LOBs with empty/null carrierInfos
    .map(lob => ({
      ...lob,
      carrierInfos: lob.carrierInfos.map(carrier => ({
        ...carrier,
        carrier: carrier.carrier === "GEICO" ? "Geico" : carrier.carrier,
        label: carrier.carrier === "GEICO" ? "GEICO"
          : !carrier.isBoldPenguinOverride && (carrier.carrier === "BoldPenguin" || carrier.carrier === "BOLD_PENGUIN") ? "Bold Penguin"
          : carrier.label,
        carrierBusinessClass: carrier.carrier === "GEICO" ? carrier.businessClass : carrier.carrierBusinessClass,
      })),
    }));
}

  function clearRedirectMessage() {
    setRedirectMessage({
      title: "",
      message: ""
    });
  }

  const waitForUserConfirmation = (
    partnerInfo: ProductCardInfo
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (partnerInfo.carrier === "Geico" && !hasBindingRestriction) {
        resolve();
        return;
      }

      const handleContinue = () => {
        setShowRedirect(false);
        clearRedirectMessage();
        resolve();
      };

      const handleClose = () => {
        setShowRedirect(false);
        clearRedirectMessage();
        reject();
      };

      const messageConfig = redirectAlertMessages(partnerInfo.carrier); 

      setRedirectMessage({
        title: messageConfig.title,
        message: (
          <div>
            <div className="modal-content">
              <p>
                {messageConfig.message}
              </p>
            </div>
          </div>
        ),
      });

      setShowRedirect(true);

      messageConfig.isRedirectable
        ? setModalFooter(
            <div className="modal-footer">
              <Button
                className="btn--primary nav-button-next"
                onClick={handleContinue}
              >
                Continue
              </Button>
            </div>
          )
        : setModalFooter(null);
      setOnModalClose(() => handleClose);
    });
  };

  return (
    <div>
      <div className="agent-partner-select-container">
        <PopupModal
          modalId="alert-modal-redirect"
          showModal={showRedirect}
          onClosed={() => {
            if (onModalClose) onModalClose();
          }}
          modalType="dialogueBox"
          autoShow={false}
          overlayShouldCloseModal={true}
          showCloseButton={true}
          modalHeadline={redirectMessage.title}
          modalContent={redirectMessage.message}
          isBackNav={false}
        >
          {modalFooter}
        </PopupModal>
        <div className="agent-partner-select-questions-container">
          <h3 style={{ marginBottom: 0 }}>Let's Get Started</h3>
          {showQuestionsAlert &&
            (questionsAlertMessage.title || questionsAlertMessage.message) && (
              <Alert
                title={questionsAlertMessage.title}
                content={questionsAlertMessage.message}
                closeAlert={() => clearAlertMessage(setQuestionsAlertMessage)}
              />
            )}
          <ZipSelectionComponent
            setShowAlert={setShowQuestionsAlert}
            setAlertMessage={setQuestionsAlertMessage}
          />
          <BusinessKeywordUkwComponent
            setShowAlert={setShowQuestionsAlert}
            setAlertMessage={setQuestionsAlertMessage}
          />
          <YesNoRadioButtonsField
            key={`bop-${quote.data.BusinessSecondaryKeyword}`}
            label={
              "Does your business own or rent a physical building, storefront, office, or warehouse?"
            }
            answer={isBOPAvailable}
            setAnswer={setIsBOPAvailable}
          />
          <YesNoRadioButtonsField
            key={`wc-${quote.data.BusinessSecondaryKeyword}`}
            label="Does your business have employees? Don't include any business owners."
            answer={isWCAvailable}
            setAnswer={setIsWCAvailable}
          />
          {isWCAvailable === false && (
            <YesNoRadioButtonsField
              key={`owners-wc-${quote.data.BusinessSecondaryKeyword}`}
              label={
                <>
                  Do you require an owner-only workers compensation policy for a contract requirement?
                  <CustomTooltip>
                    If an owner collects a salary, select Yes to WC questions.
                  </CustomTooltip>
                </>
              }
              answer={isOwnersWCAvailable}
              setAnswer={setIsOwnersWCAvailable}
            />
          )}
        </div>
        <div className="agent-partner-select-bundles-products-container">
          <div>
            {showProductsAlert &&
              (productsAlertMessage.title || productsAlertMessage.message) && (
                <Alert
                  title={productsAlertMessage.title}
                  content={productsAlertMessage.message}
                  closeAlert={() => clearAlertMessage(setProductsAlertMessage)}
                />
              )}
          </div>
          <div className="agent-partner-select-bundles">
            <AgentBundlesSection
              bundlesData={bundlesInfo}
              showBundles={showProducts}
              setShowAlert={setShowProductsAlert}
              setAlertMessage={setProductsAlertMessage}
              waitForUserConfirmation={waitForUserConfirmation}
            />
          </div>
          <div className="agent-partner-select-products">
            <AgentProductsSection
              productsData={productsInfo}
              showProducts={showProducts}
              setShowAlert={setShowProductsAlert}
              setAlertMessage={setProductsAlertMessage}
              waitForUserConfirmation={waitForUserConfirmation}
            />
          </div>
        </div>
      </div>
      <div className="agent-partner-select-footer">
      <Disclaimer
        headerLabel="Legal Notices"
        label="The information you provide will be shared with selected insurance companies to obtain a policy. If GEICO is unable to match you with one of our trusted partners, you may opt to be referred to Commercial Insurance Center (&#8220;CIC&#x201C;), to help find a commercial insurance solution for your business. Your information will not be shared with CIC without your consent."
      />
      </div>
    </div>
  );
};
